import { Seo } from '../components/Seo.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { Prose } from '../components/Prose.jsx';
import { EnquiryForm } from '../components/EnquiryForm.jsx';
import { SectionHead } from '../components/SectionHead.jsx';
import { usePage } from '../hooks/usePage.js';
import { useApi } from '../hooks/useApi.js';
import { fetchSiteSettings } from '../lib/api.js';

/**
 * Admission page steps + documents come from SiteSettings.admissionContent
 * (admin-managed). The form, banner and SEO copy stay hardcoded.
 */
const FALLBACK = {
  stepsEyebrow: 'How it works',
  stepsRawTitle: 'Four steps to <em>enrolment</em>',
  steps: [
    { title: 'Enquire',    body: 'Submit the online enquiry form, or visit one of our admission offices in Dhanmondi.' },
    { title: 'Assessment', body: 'Age-appropriate assessment and a friendly meeting with the family.' },
    { title: 'Offer',      body: 'Successful candidates receive a written offer of admission and fee schedule.' },
    { title: 'Enrol',      body: 'Submit documents, complete the registration and join the Maple Leaf community.' },
  ],
  docsEyebrow: 'Documents required',
  docsRawTitle: 'What to <em>bring</em>',
  docs: [
    'Birth certificate of the child',
    'Two passport-size photographs of the child',
    'Passport-size photograph of each parent',
    "Previous school's report card / transcript (if applicable)",
    'Transfer certificate (for transfers from another school)',
    'Photocopy of NID / passport of parents',
  ],
};

export function AdmissionPage() {
  const head = usePage('admission');
  const { data: settings } = useApi(fetchSiteSettings, []);
  const c = (settings?.admissionContent && typeof settings.admissionContent === 'object')
    ? settings.admissionContent
    : FALLBACK;
  const steps = Array.isArray(c.steps) && c.steps.length ? c.steps : FALLBACK.steps;
  const docs  = Array.isArray(c.docs)  && c.docs.length  ? c.docs  : FALLBACK.docs;

  return (
    <>
      <Seo title="Admission" description={head.metaDescription} />

      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Admission' }]}
        headline={head.headline}
        lede={head.lede}
        image={head.banner}
      />

      {/* How it works */}
      <section>
        <div className="container">
          <SectionHead
            eyebrow={c.stepsEyebrow || FALLBACK.stepsEyebrow}
            rawTitle={c.stepsRawTitle || FALLBACK.stepsRawTitle}
          />
          <div className="feature-grid">
            {steps.map((s, i) => (
              <div className="feature" key={i}>
                <span className="feature-num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission enquiry form */}
      <section className="bg-cream">
        <div className="container">
          <SectionHead
            eyebrow="Get in touch"
            rawTitle="Admission <em>enquiry</em>"
            lede="Submit the form below and our admission team will respond within two working days."
          />
          <div style={{ marginTop: 32 }}>
            <EnquiryForm />
          </div>
        </div>
      </section>

      {/* Documents required */}
      <section>
        <div className="container">
          <SectionHead
            eyebrow={c.docsEyebrow || FALLBACK.docsEyebrow}
            rawTitle={c.docsRawTitle || FALLBACK.docsRawTitle}
          />
          <div style={{ marginTop: 24 }}>
            <Prose fullWidth>
              <ul>
                {docs.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </Prose>
          </div>
        </div>
      </section>
    </>
  );
}
